import { ChevronDown, MessageSquare, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';
import { Input } from '../../../components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.js';
import { cn } from '../../../lib/utils.js';
import { TeamsDropdown, TEAM_ALL } from '../../common/components/TeamsDropdown.js';
import { KpiChip } from '../../kpi/components/KpiChip.js';
import {
  presetToDateRange,
  rollupByScenarioMetric,
  useKpiKeys,
  useKpiMetrics,
  useKpiTypes,
  useKpiValues,
} from '../../kpi/hooks.js';
import { asKpiMetric, asKpiType, type KpiMetric, type KpiType } from '../../kpi/types.js';
import { FusionRequestSheet } from '../../scenario/components/FusionRequestSheet.js';
import {
  applyScenarioFilter,
  decodeScenarioFilter,
  encodeScenarioFilter,
  ScenarioFilterDrawer,
  type ScenarioFilterValue,
} from '../../scenario/components/ScenarioFilterDrawer.js';
import { FOLDER_ALL, FOLDER_UNCATEGORIZED } from '../../scenario/components/ScenarioPageHeader.js';
import { useScenariosList } from '../../scenario/hooks.js';
import { asScenarioRow } from '../../scenario/types.js';
import { ValuePagesToggle } from '../components/ValuePagesToggle.js';

type StatusFilter = 'all' | 'active' | 'inactive';

/**
 * Value · Scenario KPIs. Mirrors the legacy
 * `wf-audit/src/slices/value/pages/scenario-kpis.tsx` cross-table —
 * one row per scenario with attached metrics rendered as colour-coded
 * chips. Scenarios with zero attached KPIs filter out by default (the
 * "Show all" toggle re-includes them). Clicking a scenario name opens
 * the scenarios-page detail modal via the `?scenarioId=` deep link.
 */
export function ValueScenarioKpisPage(): JSX.Element {
  const { data: scenarioRows = [], isLoading, error } = useScenariosList();
  const { data: metricRows = [] } = useKpiMetrics();
  const { data: typeRows = [] } = useKpiTypes();
  const { data: keyRows = [] } = useKpiKeys();
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('__all__');
  const [metricFilter, setMetricFilter] = useState<string>('__all__');
  const [params, setParams] = useSearchParams();
  const teamFilterFromQuery = params.get('team') ?? TEAM_ALL;
  // Folder filter + drawer filter — URL-pinned for parity with the
  // Scenarios / Value Metrics / Dashboard / Presentation pages.
  const folder = params.get('folder') ?? FOLDER_ALL;
  const filterEncoded = params.get('filter');
  const filter = useMemo<ScenarioFilterValue>(
    () => decodeScenarioFilter(filterEncoded),
    [filterEncoded],
  );
  const [status, setStatus] = useState<StatusFilter>('all');
  const [fusionRequestOpen, setFusionRequestOpen] = useState(false);

  function setTeam(next: string): void {
    const ps = new URLSearchParams(params);
    if (next === TEAM_ALL) ps.delete('team');
    else ps.set('team', next);
    setParams(ps);
  }

  function setFolder(next: string): void {
    const ps = new URLSearchParams(params);
    if (next === FOLDER_ALL) ps.delete('folder');
    else ps.set('folder', next);
    setParams(ps);
  }

  function setFilter(next: ScenarioFilterValue): void {
    const ps = new URLSearchParams(params);
    const encoded = encodeScenarioFilter(next);
    if (!encoded) ps.delete('filter');
    else ps.set('filter', encoded);
    setParams(ps);
  }

  // KPI rollup — drives the Quantity / Amount columns. Fixed at the
  // 30-day window matching the legacy default until a custom-range
  // picker lands on the filter row.
  const { from: kpiFrom, to: kpiTo } = useMemo(() => presetToDateRange('last-30-days'), []);
  const { data: kpiValues = [] } = useKpiValues({
    from: kpiFrom,
    to: kpiTo,
    aggregationType: 'day',
  });
  const rollup = useMemo(() => rollupByScenarioMetric(kpiValues), [kpiValues]);

  const metrics = useMemo(() => metricRows.map(asKpiMetric), [metricRows]);
  const types = useMemo(() => typeRows.map(asKpiType), [typeRows]);

  const metricById = useMemo(() => {
    const m = new Map<number, KpiMetric>();
    for (const x of metrics) m.set(x.id, x);
    return m;
  }, [metrics]);

  const typeById = useMemo(() => {
    const m = new Map<number, KpiType>();
    for (const x of types) m.set(x.id, x);
    return m;
  }, [types]);

  // fusionScenarioId → array of attached metric ids (deduped).
  const metricsByScenario = useMemo(() => {
    const m = new Map<string, Set<number>>();
    for (const k of keyRows) {
      const fusionScenarioId = typeof k.fusionScenarioId === 'string' ? k.fusionScenarioId : null;
      if (!fusionScenarioId) continue;
      const attached = Array.isArray(k.metrics) ? (k.metrics as unknown[]) : [];
      const set = m.get(fusionScenarioId) ?? new Set<number>();
      for (const x of attached) {
        const id = typeof x === 'number' ? x : Number(x);
        if (Number.isFinite(id)) set.add(id);
      }
      m.set(fusionScenarioId, set);
    }
    return m;
  }, [keyRows]);

  // Flatten to one row per (scenario × metric) — matches the legacy
  // 6-column table (`# | Scenario | KPI Type | KPI Metric | Quantity |
  // Amount`). Scenarios with no attached metrics emit a single row
  // with empty KPI columns when `showAll` is on; otherwise they
  // filter out.
  interface FlatRow {
    rowKey: string;
    fusionId: string;
    scenarioName: string;
    folderName: string;
    scenarioStatus: string;
    teamFusionId: string;
    metric: KpiMetric | null;
    metricType: KpiType | null;
  }

  // Drawer filter applies against the full ScenarioRow shape — map
  // once and pass through `applyScenarioFilter`. Build a Set of
  // surviving fusionIds and use it inside the flat-row loop below.
  const eligibleFusionIds = useMemo<ReadonlySet<string>>(() => {
    const narrowed = applyScenarioFilter(scenarioRows.map(asScenarioRow), filter);
    return new Set(narrowed.map((r) => r.fusionId));
  }, [scenarioRows, filter]);

  const flatRows = useMemo<ReadonlyArray<FlatRow>>(() => {
    const q = search.trim().toLowerCase();
    const typeIdFilter = typeFilter === '__all__' ? null : Number(typeFilter);
    const metricIdFilter = metricFilter === '__all__' ? null : Number(metricFilter);
    const out: FlatRow[] = [];
    for (const row of scenarioRows) {
      const fusionId = typeof row.fusionId === 'string' ? row.fusionId : '';
      const name = typeof row.name === 'string' ? row.name : '';
      const folderName = typeof row.folderName === 'string' ? row.folderName : '';
      const scenarioStatus = typeof row.status === 'string' ? row.status : '';
      const teamFusionId = typeof row.teamFusionId === 'string' ? row.teamFusionId : '';
      if (!fusionId || !name) continue;
      if (!eligibleFusionIds.has(fusionId)) continue;
      if (status === 'active' && scenarioStatus !== 'On') continue;
      if (status === 'inactive' && scenarioStatus === 'On') continue;
      if (teamFilterFromQuery !== TEAM_ALL && teamFusionId !== teamFilterFromQuery) continue;
      if (folder === FOLDER_UNCATEGORIZED) {
        if (folderName.length > 0) continue;
      } else if (folder !== FOLDER_ALL) {
        if (folderName !== folder) continue;
      }
      const attached = metricsByScenario.get(fusionId);
      const ids = attached ? [...attached] : [];
      const attachedMetrics = ids
        .map((id) => metricById.get(id))
        .filter((m): m is KpiMetric => m !== undefined);
      if (attachedMetrics.length === 0) {
        if (!showAll) continue;
        if (typeIdFilter !== null || metricIdFilter !== null) continue;
        if (q) {
          if (!name.toLowerCase().includes(q) && !folderName.toLowerCase().includes(q)) continue;
        }
        out.push({
          rowKey: `${fusionId}::none`,
          fusionId,
          scenarioName: name,
          folderName,
          scenarioStatus,
          teamFusionId,
          metric: null,
          metricType: null,
        });
        continue;
      }
      for (const m of attachedMetrics) {
        if (typeIdFilter !== null && m.kpiTypeId !== typeIdFilter) continue;
        if (metricIdFilter !== null && m.id !== metricIdFilter) continue;
        if (q) {
          const hit =
            name.toLowerCase().includes(q) ||
            folderName.toLowerCase().includes(q) ||
            m.displayName.toLowerCase().includes(q) ||
            (m.internalName ?? '').toLowerCase().includes(q);
          if (!hit) continue;
        }
        out.push({
          rowKey: `${fusionId}::${m.id}`,
          fusionId,
          scenarioName: name,
          folderName,
          scenarioStatus,
          teamFusionId,
          metric: m,
          metricType: m.kpiTypeId ? (typeById.get(m.kpiTypeId) ?? null) : null,
        });
      }
    }
    return out.sort((a, b) => a.scenarioName.localeCompare(b.scenarioName));
  }, [
    scenarioRows,
    metricsByScenario,
    metricById,
    typeById,
    search,
    showAll,
    typeFilter,
    metricFilter,
    status,
    teamFilterFromQuery,
    eligibleFusionIds,
    folder,
  ]);

  // All / Active / Inactive counts over the pre-status set.
  const counts = useMemo(() => {
    let active = 0;
    let inactive = 0;
    for (const row of scenarioRows) {
      if (row.status === 'On') active += 1;
      else inactive += 1;
    }
    return { all: scenarioRows.length, active, inactive };
  }, [scenarioRows]);

  // Distinct folder names actually present on the license's
  // scenarios — populates the folder dropdown.
  const folderOptions = useMemo<ReadonlyArray<string>>(() => {
    const names = new Set<string>();
    for (const row of scenarioRows) {
      const folderName = typeof row.folderName === 'string' ? row.folderName : '';
      if (folderName.length > 0) names.add(folderName);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [scenarioRows]);

  const folderLabel =
    folder === FOLDER_ALL
      ? 'All scenarios'
      : folder === FOLDER_UNCATEGORIZED
        ? 'Uncategorized'
        : folder;

  // ScenarioFilterDrawer needs an array of ScenarioRow to populate
  // its multi-selects (apps / tags / modifiedBy). Cheap to compute
  // again here in the JSX-ready shape.
  const drawerRows = useMemo(() => scenarioRows.map(asScenarioRow), [scenarioRows]);

  // When type filter changes, drop a metric selection that doesn't
  // belong to the chosen type — keeps the two dropdowns coherent so
  // the operator doesn't see an empty table for an impossible combo.
  const metricsForActiveType = useMemo(() => {
    if (typeFilter === '__all__') return metrics;
    const tid = Number(typeFilter);
    return metrics.filter((m) => m.kpiTypeId === tid);
  }, [metrics, typeFilter]);
  const activeFilterCount =
    (typeFilter === '__all__' ? 0 : 1) + (metricFilter === '__all__' ? 0 : 1);

  return (
    <div className="relative z-10 flex h-full flex-col">
      {/* Header — page title, segmented Value-pages toggle (center),
          Teams selector + Fusion Request (right). Matches the legacy
          layout from `sec_215`. */}
      <header className="flex flex-wrap items-center gap-3 px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Value:</h1>
        <span className="text-foreground/30">|</span>
        {/* Folder dropdown — same trigger + sentinels as the
            Scenarios / Value Metrics / Dashboard / Presentation
            pages. URL-pinned at `?folder=`. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-base text-foreground/80 transition-colors hover:border-foreground/15 hover:bg-foreground/5 hover:text-foreground"
            >
              <span>{folderLabel}</span>
              <ChevronDown className="h-4 w-4 text-foreground/50" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[60vh] min-w-[220px] overflow-y-auto">
            <DropdownMenuLabel>Folder</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={folder === FOLDER_ALL}
              onCheckedChange={() => setFolder(FOLDER_ALL)}
            >
              All scenarios
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={folder === FOLDER_UNCATEGORIZED}
              onCheckedChange={() => setFolder(FOLDER_UNCATEGORIZED)}
            >
              Uncategorized
            </DropdownMenuCheckboxItem>
            {folderOptions.length > 0 ? <DropdownMenuSeparator /> : null}
            {folderOptions.map((name) => (
              <DropdownMenuCheckboxItem
                key={name}
                checked={folder === name}
                onCheckedChange={() => setFolder(name)}
              >
                {name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex-1" />
        <ValuePagesToggle />
        <div className="flex-1" />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setFusionRequestOpen(true)}
          className="border-foreground/15 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
        >
          <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Fusion Request
        </Button>
      </header>

      {/* Primary toolbar — 3-col grid for true centering of the
          status pills (matches the layout shipped on the other
          pages). Search + filter drawer on the left, pills in the
          middle, Teams dropdown on the right. */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-foreground/10 bg-foreground/[0.03] px-6 pt-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/40" />
            <Input
              type="search"
              placeholder="Search scenarios or metrics..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-foreground/15 bg-foreground/5 pl-9 text-foreground placeholder:text-foreground/40 focus-visible:ring-white/30"
            />
          </div>
          <ScenarioFilterDrawer value={filter} onChange={setFilter} rows={drawerRows} />
        </div>
        {/* All / Active / Inactive counter pills — legacy parity. */}
        <div className="inline-flex rounded-md border border-foreground/15 bg-foreground/[0.04] p-1">
          {(['all', 'active', 'inactive'] as const).map((k) => {
            const sel = status === k;
            const count =
              k === 'all' ? counts.all : k === 'active' ? counts.active : counts.inactive;
            return (
              <button
                key={k}
                type="button"
                onClick={() => setStatus(k)}
                className={cn(
                  'flex items-center gap-2 rounded px-3 py-1 text-sm capitalize transition-colors',
                  sel
                    ? 'bg-foreground/15 text-foreground'
                    : 'text-foreground/70 hover:text-foreground',
                )}
              >
                <span>{k}</span>
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-xs',
                    sel
                      ? 'bg-foreground/20 text-foreground'
                      : 'bg-foreground/10 text-foreground/60',
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex min-w-0 items-center justify-end">
          <TeamsDropdown team={teamFilterFromQuery} onTeamChange={setTeam} />
        </div>
      </div>

      {/* KPI-specific filters — separate row so the primary toolbar
          (search + status + team) stays uncluttered. Types narrow
          the Metrics dropdown; Clear-filters resets both. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-foreground/10 bg-foreground/[0.03] px-6 pb-3 pt-2">
        <Select
          value={typeFilter}
          onValueChange={(v) => {
            setTypeFilter(v);
            // Reset metric filter when type changes — see comment near
            // `metricsForActiveType` for the rationale.
            if (v !== '__all__' && metricFilter !== '__all__') {
              const tid = Number(v);
              const stillValid = metrics.find((m) => m.id === Number(metricFilter))?.kpiTypeId;
              if (stillValid !== tid) setMetricFilter('__all__');
            }
          }}
        >
          <SelectTrigger className="w-44 border-foreground/15 bg-foreground/5 text-foreground/80">
            <SelectValue placeholder="Filter by KPI type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            {types.map((t) => (
              <SelectItem key={t.id} value={String(t.id)}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={metricFilter} onValueChange={setMetricFilter}>
          <SelectTrigger className="w-52 border-foreground/15 bg-foreground/5 text-foreground/80">
            <SelectValue placeholder="Filter by metric" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All metrics</SelectItem>
            {metricsForActiveType.map((m) => (
              <SelectItem key={m.id} value={String(m.id)}>
                {m.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Show-w/o-KPIs lives next to the Metric dropdown — same
            visual grouping the operator asked for. */}
        <label className="inline-flex items-center gap-2 text-xs text-foreground/65">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-foreground/30"
          />
          Show w/o KPIs
        </label>
        {activeFilterCount > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setTypeFilter('__all__');
              setMetricFilter('__all__');
            }}
            className="text-foreground/70 hover:bg-foreground/10 hover:text-foreground"
          >
            <X className="mr-1 h-4 w-4" /> Clear filters ({activeFilterCount})
          </Button>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        {error ? (
          <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
            Could not load scenarios: {error.message}
          </div>
        ) : isLoading ? (
          <div className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-8 text-center text-sm text-foreground/50">
            Loading…
          </div>
        ) : flatRows.length === 0 ? (
          <div className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-8 text-center text-sm text-foreground/50">
            No KPI data available.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-foreground/10 bg-foreground/[0.02]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-foreground/10 text-xs uppercase tracking-wide text-foreground/55">
                  <th className="w-12 px-3 py-2 text-left font-medium">#</th>
                  <th className="px-3 py-2 text-left font-medium">Scenario</th>
                  <th className="bg-blue-500/10 px-3 py-2 text-left font-medium text-foreground">
                    KPI Type
                  </th>
                  <th className="bg-rose-500/10 px-3 py-2 text-left font-medium text-foreground">
                    KPI Metric
                  </th>
                  <th className="px-3 py-2 text-right font-medium">Quantity</th>
                  <th className="bg-orange-700/10 px-3 py-2 text-right font-medium text-foreground">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="text-foreground/85">
                {flatRows.map((row, i) => (
                  <tr key={row.rowKey} className="border-b border-foreground/5 align-top">
                    <td className="px-3 py-2.5 text-foreground/55">{i + 1}</td>
                    <td className="px-3 py-2.5">
                      <Link
                        to={`/scenarios?scenarioId=${encodeURIComponent(row.fusionId)}`}
                        className="text-foreground hover:underline"
                      >
                        {row.scenarioName}
                      </Link>
                      {row.folderName ? (
                        <div className="text-xs text-foreground/40">{row.folderName}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5">
                      {row.metricType ? (
                        <span className="text-foreground/85">{row.metricType.name}</span>
                      ) : (
                        <span className="text-xs italic text-foreground/35">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {row.metric ? (
                        <span className="inline-flex items-center gap-1.5">
                          <KpiChip icon={row.metric.icon} color={row.metric.color} size="sm" />
                          <span className="text-foreground/85">{row.metric.displayName}</span>
                        </span>
                      ) : (
                        <span className="text-xs italic text-foreground/35">No KPI attached</span>
                      )}
                    </td>
                    {(() => {
                      const totals = row.metric
                        ? (rollup.get(row.fusionId)?.get(String(row.metric.id)) ?? null)
                        : null;
                      return (
                        <>
                          <td
                            className={
                              'px-3 py-2.5 text-right font-mono ' +
                              (totals ? 'text-foreground/85' : 'text-foreground/40')
                            }
                          >
                            {totals ? totals.quantity.toLocaleString() : '—'}
                          </td>
                          <td
                            className={
                              'px-3 py-2.5 text-right font-mono ' +
                              (totals ? 'text-foreground/85' : 'text-foreground/40')
                            }
                          >
                            {totals ? `$${totals.value.toLocaleString()}` : '—'}
                          </td>
                        </>
                      );
                    })()}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="border-t border-foreground/10 px-3 py-2 text-[11px] italic text-foreground/40">
              Quantity / Amount columns roll up the last 30 days of {`${kpiFrom} → ${kpiTo}`} data
              from <code>/v1/kpi/values</code>.
            </p>
          </div>
        )}
      </div>

      <FusionRequestSheet open={fusionRequestOpen} onOpenChange={setFusionRequestOpen} />
    </div>
  );
}
